// Data requests page (review W29): the customer data requests Shopify passed
// to Datacrow, each with a download of the stored events that carry that
// customer's identifiers (src/lib/gdpr.ts; the copy says exactly that, review
// S1). The merchant reviews the file and sends it to the customer to answer
// the request within Shopify's 30 days.
//
// App renders this for /data-requests BEFORE the onboarding and plan-picker
// gates: answering a data request is a legal duty, so no paywall or unfinished
// setup may hide it, and the notice email's deep link must always land.
//
// The list names shoppers, so it never goes into the SWR cache (api.ts
// shouldCache), and the file downloads as a blob that never touches it.
// No router hooks, like Billing and Destinations, so it previews standalone.

import { useEffect, useState } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Badge,
  Spinner,
  Divider,
  EmptyState,
} from "@shopify/polaris";
import { apiDownload, apiFetch, ApiError } from "./api";
import {
  customerLabel,
  fileBuiltLabel,
  FILE_READY_TOAST,
  type DataRequestList,
  type DataRequestView,
} from "./data-requests";
import { daysUntil, formatDate } from "./dates";

interface DataRequestsProps {
  shop: string;
  onBack?: () => void;
}

export function DataRequests({ shop, onBack }: DataRequestsProps) {
  const shopify = useAppBridge();
  const [requests, setRequests] = useState<DataRequestView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const resp = await apiFetch<DataRequestList>(shopify, "/api/data-requests");
      setRequests(resp.requests);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : (err as Error).message);
    }
  };

  useEffect(() => {
    void reload();
    // shopify is stable per App Bridge contract; reload identity isn't a dep here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (r: DataRequestView) => {
    setDownloading(r.id);
    setDownloadError(null);
    try {
      await apiDownload(
        shopify,
        `/api/data-requests/${encodeURIComponent(r.id)}/export`,
        `datacrow-customer-data-${r.id}.json`,
      );
      // "Ready", not "downloaded": the page can't see the save (review S22).
      shopify.toast.show(FILE_READY_TOAST);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setDownloadError("This request is gone: it passed its 30 days, or the customer's data was erased.");
      } else if (err instanceof ApiError) {
        setDownloadError(`Couldn't build the file (${err.message}). Try again, or email support@datacrow.app.`);
      } else {
        setDownloadError(
          `Couldn't download the file (${(err as Error).message}). Try again, or email support@datacrow.app.`,
        );
      }
    } finally {
      setDownloading(null);
      // The badge moves to "File built", or a gone request drops off.
      void reload();
    }
  };

  const backAction = onBack ? { content: "Back", onAction: onBack } : undefined;

  let body;
  if (loadError) {
    body = (
      <Banner tone="critical" title="Couldn't load data requests">
        <p>{loadError}</p>
        <p>
          <Button onClick={() => void reload()}>Retry</Button>
        </p>
      </Banner>
    );
  } else if (requests === null) {
    body = (
      <Card>
        <BlockStack gap="200" align="center">
          <Spinner accessibilityLabel="Loading data requests" size="large" />
          <Text as="p" tone="subdued">
            Loading…
          </Text>
        </BlockStack>
      </Card>
    );
  } else if (requests.length === 0) {
    body = (
      <Card>
        <EmptyState
          heading="No data requests"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>
            When a customer asks your store for their data, the request shows up here and
            Datacrow emails the store owner.
          </p>
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <Card>
        <BlockStack gap="300">
          {requests.map((r, i) => (
            <BlockStack key={r.id} gap="300">
              {i > 0 ? <Divider /> : null}
              <RequestRow
                request={r}
                downloading={downloading === r.id}
                disabled={downloading !== null && downloading !== r.id}
                onDownload={() => void download(r)}
              />
            </BlockStack>
          ))}
        </BlockStack>
      </Card>
    );
  }

  return (
    <Page title="Data requests" subtitle={shop} backAction={backAction}>
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="200">
            <Text as="p">
              When a customer asks your store for their data, Shopify passes the request to
              Datacrow and it shows up here. Download the file, review it, then send it to the
              customer.
            </Text>
            <Text as="p">
              Datacrow builds the file when you click, from the stored events and failed-delivery
              copies that match the customer&apos;s email, phone, customer ID or order number.
              Browsing that only carries an anonymous browser ID is not included, and neither is an
              entry matched only by email or phone that names a different customer. The file&apos;s
              note counts the entries left out that way. If nothing matches, the note says so and
              the lists are empty.
            </Text>
            <Text as="p" tone="subdued">
              Answer each request by its date. Datacrow deletes the request that day, 30 days after
              it arrived.
            </Text>
          </BlockStack>
        </Card>
        {downloadError && (
          <Banner tone="critical" onDismiss={() => setDownloadError(null)}>
            <p>{downloadError}</p>
          </Banner>
        )}
        {body}
      </BlockStack>
    </Page>
  );
}

function RequestRow({
  request,
  downloading,
  disabled,
  onDownload,
}: {
  request: DataRequestView;
  downloading: boolean;
  disabled: boolean;
  onDownload: () => void;
}) {
  // Stamped when the server builds the file, not when the browser saves it,
  // so the badge says "File built" (review S22).
  const builtLabel = fileBuiltLabel(request);
  const built = builtLabel !== null;
  // Days left rounds up (dates.ts), so 1 means under a day.
  const days = daysUntil(request.answerBy);
  const due =
    !built && days <= 7 ? (
      <Badge tone={days <= 2 ? "critical" : "warning"}>
        {days <= 1 ? "Due in less than a day" : `Due in ${days} days`}
      </Badge>
    ) : null;
  return (
    <InlineStack align="space-between" blockAlign="center" gap="300">
      <BlockStack gap="100">
        <Text as="h3" variant="headingSm">
          {customerLabel(request)}
        </Text>
        <Text as="p" tone="subdued" variant="bodySm">
          Answer by {formatDate(request.answerBy)}
        </Text>
      </BlockStack>
      <InlineStack gap="200" blockAlign="center">
        {due}
        {builtLabel !== null ? (
          <Badge tone="info">{builtLabel}</Badge>
        ) : (
          <Badge tone="attention">No file yet</Badge>
        )}
        <Button onClick={onDownload} loading={downloading} disabled={disabled}>
          Download
        </Button>
      </InlineStack>
    </InlineStack>
  );
}
